import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Users, 
  Shield, 
  Award, 
  BookOpen, 
  Star,
  ArrowRight,
  CheckCircle,
  MapPin,
  Clock
} from 'lucide-react';

const About = () => {
  const visionPoints = [
    "Every child feels safe, valued, and inspired to explore",
    "Nurture emotional well-being and cognitive development", 
    "Foster a love for learning that lasts a lifetime",
    "Empower children to be curious, resilient, and kind"
  ];

  const missionPoints = [
    {
      icon: Shield,
      title: "Safe & Secure Environment",
      description: "Provide a warm and secure environment where children feel emotionally safe and supported."
    },
    {
      icon: BookOpen,
      title: "Play-Based Exploration", 
      description: "Encourage play-based exploration that fosters creativity and independent thinking."
    },
    {
      icon: Users,
      title: "Family Partnership",
      description: "Partner with families to ensure consistent, respectful care and open communication."
    },
    {
      icon: Heart,
      title: "Celebrate Diversity",
      description: "Celebrate diversity and promote inclusion for all children, regardless of background or ability."
    },
    {
      icon: Star,
      title: "Unique Development",
      description: "Support each child's unique developmental journey through observation, engagement, and love."
    }
  ];

  const staffQualifications = [
    "Certified Early Childhood Educators (ECE)",
    "Infant Toddler Educator (ITE) qualifications", 
    "Valid First Aid certification for children",
    "ECEBC-recognized program graduates",
    "Ongoing professional development training",
    "Specialized training in child development and inclusive care"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-purple-500 text-white text-base px-4 py-2 mb-6">
              Our Story
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-purple-600 mb-6">
              About Maple Kids Academy
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              A warm, nurturing environment where every child feels valued, respected, 
              and excited to explore their world through play-based learning.
            </p>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                At Maple Kids Academy, we envision a world where every child feels safe, valued, 
                and inspired to explore, create, and grow into confident, capable individuals. 
                We aim to nurture emotional well-being, cognitive development, and a love for learning 
                that lasts a lifetime.
              </p>

              <div className="space-y-4">
                {visionPoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-primary mt-1 flex-shrink-0" />
                    <p className="text-gray-700">{point}</p>
                  </div>
                ))}
              </div>

              <p className="text-lg text-gray-600 mt-8 leading-relaxed">
                Through meaningful relationships and hands-on experiences, we empower children 
                to be curious, resilient, and kind.
              </p>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1484820540004-14229fe36ca4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjaGlsZGNhcmV8ZW58MHx8fHwxNzU2MDE1NjUyfDA&ixlib=rb-4.1.0&q=85" 
                alt="Child building blocks showing development and learning" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-rainbow-blue text-white p-4 rounded-full shadow-lg">
                <BookOpen className="h-8 w-8" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-600 mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Our mission guides everything we do - from daily interactions to program planning, 
              ensuring every child receives the respectful, nurturing care they deserve.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {missionPoints.map((point, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 bg-white border-2 border-transparent hover:border-purple-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-4 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl group-hover:from-purple-100 group-hover:to-pink-100 transition-all duration-300">
                      <point.icon className="h-6 w-6 text-orange-600 group-hover:text-purple-600 transition-colors duration-300" />
                    </div>
                  </div>
                  <CardTitle className="text-lg text-purple-700 group-hover:text-purple-800 transition-colors">{point.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {point.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Qualifications */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600880291319-1a7499c191e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjaGlsZGNhcmV8ZW58MHx8fHwxNzU2MDE1NjUyfDA&ixlib=rb-4.1.0&q=85" 
                alt="Professional childcare educator with toddler" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute -top-6 -left-6 bg-rainbow-green text-white p-4 rounded-full shadow-lg">
                <Award className="h-8 w-8" />
              </div>
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Qualified & Caring Staff
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                All staff at Maple Kids Academy are certified Early Childhood Educators or 
                Infant Toddler Educators who have received their credentials from ECEBC-recognized programs. 
                Our team is dedicated to providing a caring, nurturing, and safe environment for children 
                to grow and flourish every day.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {staffQualifications.map((qualification, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-primary mt-1 flex-shrink-0" />
                    <p className="text-gray-700">{qualification}</p>
                  </div>
                ))}
              </div>

              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We are committed to professional development and support our staff with training 
                opportunities to stay current in best practices. Every team member holds valid 
                First Aid certification for children.
              </p>

              <Button asChild className="bg-blue-primary hover:bg-blue-600">
                <Link to="/contact">
                  Meet Our Team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Inclusion Philosophy */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Inclusion & Support
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Maple Kids Academy embraces all children, regardless of ability, background, or developmental needs. 
              We create inclusive learning environments where every child feels a sense of belonging.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Users className="h-12 w-12 text-blue-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-navy-primary mb-2">Collaborative Care</h3>
                  <p className="text-gray-600 text-sm">
                    We work in partnership with Supported Child Development programs 
                    and other professionals to ensure children receive the resources they need.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Heart className="h-12 w-12 text-blue-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-navy-primary mb-2">Celebrate Diversity</h3>
                  <p className="text-gray-600 text-sm">
                    We celebrate diversity and promote empathy, understanding, and friendship 
                    in all that we do.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Star className="h-12 w-12 text-blue-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-navy-primary mb-2">Individual Support</h3>
                  <p className="text-gray-600 text-sm">
                    Educators observe and document children's development, 
                    and collaborate with families to provide responsive, individualized care.
                  </p>
                </CardContent>
              </Card>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed">
              We value family culture, language, and traditions, and encourage families to be active 
              participants—whether through sharing stories, attending events, or contributing to classroom activities. 
              When families and educators work together, children thrive.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-pink-500 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Come Visit Our Happy Place!
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0 text-yellow-300" />
                  <div>
                    <p className="font-bold text-lg">10681 248 St, Maple Ridge, BC</p>
                    <p className="text-purple-100">Easy parking for families</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <Clock className="h-5 w-5 mt-1 flex-shrink-0 text-green-300" />
                  <div>
                    <p className="font-bold text-lg">Monday - Friday: 7:00 AM - 5:00 PM</p>
                    <p className="text-purple-100">Closed weekends and holidays</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Link to="/contact">Book Your Visit</Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white/20 hover:bg-white/30 text-white border-2 border-white hover:border-yellow-300 font-bold px-8 py-4 rounded-full transition-all"
                >
                  <Link to="/enroll">Join Our Family</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30">
                <h3 className="text-2xl font-bold mb-6 text-center">
                  Why Choose Maple Kids?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Small groups, big love</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                      <Shield className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Licensed & insured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-pink-400 rounded-full flex items-center justify-center">
                      <Heart className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Every child is welcome</span>
                  </div>
                </div>
                
                <div className="mt-6 text-center bg-white/20 p-4 rounded-xl">
                  <p className="text-sm font-medium">Ready to join our family?</p>
                  <p className="text-xs text-purple-100 mt-1">Tours available by appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;